import { connect, Dispatch } from 'react-redux';
import AbstractDriveItemListPage, { AbstractDriveItemListPageProps } from '../abstract';
import { StoreState } from '../../../reducers';
import { getOrFetchDriveItemMeta, fetchContentBlock } from '../../DriveItemList/actions';
import UserAgentUtils from '../../../utils/userAgent';
import { FetchStatus, getDriveItemStatusFromMap } from '../../DriveItemList/reducer';
import { myDocumentsNavItems, NavigationItemData } from '../constants';
import Utils from '../../../utils';
import { Api } from '../../../typings/api';

interface FolderDriveItemListPageProps extends AbstractDriveItemListPageProps {
	dispatch: Dispatch<StoreState>;
	privateDirId: number | undefined;
	sharedDirId: number | undefined;
	sourcesDirId: number | undefined;
	archiveDirId: number | undefined;
	locale: string;
	specialPrivateDirIds: number[];
}

export class FolderDriveItemListPage extends AbstractDriveItemListPage<FolderDriveItemListPageProps> {

	headerLabel = 'messages.folder';
	noItemsLabel = 'messages.no_assets';

	componentWillReceiveProps(nextProps: FolderDriveItemListPageProps) {
		const { params, privateDirId, sharedDirId } = this.props;
		if (
			params !== nextProps.params
			|| privateDirId !== nextProps.privateDirId
			|| sharedDirId !== nextProps.sharedDirId
		) {
			this.initializeView(nextProps);
		}
	}

	initializeView(props: FolderDriveItemListPageProps): void {
		const driveFolderId = this.getDriveFolderId(props);
		const isSharedDir = this.isSharedDir(props);
		const { driveList: { driveItems, driveItemsFetchStatus }, dispatch } = props;
		const isFolderLoaded = driveFolderId && !!driveItems[driveFolderId];
		const driveFolderFetchStatus = driveFolderId ? getDriveItemStatusFromMap(driveItemsFetchStatus, driveFolderId) : null;
		const isFolderMetaFetched = driveFolderFetchStatus && driveFolderFetchStatus.metaFetchStatus === FetchStatus.FETCHED;

		// In mobile app mode when online, fetch folders on-demand if not already loaded
		// This allows navigation to work while getAllDriveItems() runs in the background
		const shouldFetchFolder = driveFolderId && (
			!UserAgentUtils.isMobileApp ||
			isSharedDir ||
			(UserAgentUtils.isOnline() && !isFolderLoaded)
		);

		if (shouldFetchFolder && driveFolderId) {
			dispatch(getOrFetchDriveItemMeta(driveFolderId, true, true, isSharedDir))
				.then((folderMeta: Api.DriveItem | {}) => {
					// Once folder metadata is loaded, fetch the first content block to get the items
					// This ensures items appear immediately when navigating to a folder
					// Cast to DriveFolderInfo since folders have the resources property
					const driveFolder = folderMeta as Api.DriveFolderInfo;
					if (driveFolder && driveFolder.id && driveFolder.resources && driveFolder.resources.length > 0 && UserAgentUtils.isOnline() && driveFolderId) {
						const resolutionsBlock = driveFolder.resolutionsBlock || 1;
						const firstResource = driveFolder.resources[0];
						const firstBlock = firstResource && firstResource.block ? firstResource.block : resolutionsBlock;
						// Fetch the first content block to load the items
						dispatch(fetchContentBlock(driveFolderId, firstBlock));
					}
				})
				.catch(() => {
					// Error handling is done in getOrFetchDriveItemMeta
				});
		} else if (isFolderMetaFetched && UserAgentUtils.isOnline() && driveFolderId) {
			// Folder metadata is loaded but content block might not be fetched yet
			// Fetch the first content block if needed
			const driveFolder = driveItems[driveFolderId];
			if (driveFolder && driveFolder.resources && driveFolder.resources.length > 0) {
				const resolutionsBlock = driveFolder.resolutionsBlock || 1;
				const firstResource = driveFolder.resources[0];
				const firstBlock = firstResource && firstResource.block ? firstResource.block : resolutionsBlock;
				const firstBlockStatus = driveFolderFetchStatus && driveFolderFetchStatus.contentBlocksStatus[firstBlock];

				// Fetch first content block if not already fetched or fetching
				if (firstBlockStatus !== FetchStatus.FETCHED && firstBlockStatus !== FetchStatus.FETCHING) {
					dispatch(fetchContentBlock(driveFolderId, firstBlock));
				}
			}
		}

		this.setState({
			navigationItems: this.getNavigationItems(props)
		});
	}

	getDriveItemIds(): number[] {
		const { driveList: { driveItems: driveItemsStore }, sharedDirId, sourcesDirId, archiveDirId} = this.props;
		const driveFolderId = this.getDriveFolderId();
		const isPrivateDir = this.isPrivateDir(this.props);
		if (driveFolderId) {
			const driveFolder = driveItemsStore[driveFolderId];

			if (driveFolder) {
				const { resources } = driveFolder;
				if (resources) {
					let additionalFolders = isPrivateDir
						? [sharedDirId, sourcesDirId, archiveDirId].filter(Boolean) as number[]
						: [];
					if (this.isSpecialPrivateDir(driveFolderId)) {
						additionalFolders = [];
					}
					return [
						...additionalFolders,
						...resources
							.map(resource => resource.id)
							.filter(this.filterOutMyDocumentsFromRoot)
					];
				}
			}
		}
		return [];
	}

	filterOutMyDocumentsFromRoot = (resourceId: number): boolean => {
		const { galleryId, privateDirId } = this.props;
		const driveFolderId = this.getDriveFolderId();
		const isMyDocumentsInRoot = driveFolderId === galleryId && resourceId === privateDirId;
		return !(isMyDocumentsInRoot && !this.isSpecialPrivateDir(resourceId));
	}

	isFetching(driveItemIds: number[]): boolean {
		const driveFolderId = this.getDriveFolderId();

		// In mobile app mode when online, check fetching status to show loading state
		// This allows proper feedback while folders are being fetched on-demand
		if (driveFolderId && (!UserAgentUtils.isMobileApp || UserAgentUtils.isOnline())) {
			const { driveItemsFetchStatus } = this.props.driveList;
			const driveFolderFetchStatus = getDriveItemStatusFromMap(driveItemsFetchStatus, driveFolderId);
			const { contentBlocksStatus, metaFetchStatus } = driveFolderFetchStatus;

			if (metaFetchStatus !== FetchStatus.FETCHED && metaFetchStatus !== FetchStatus.ERROR) {
				return true;
			}

			const blocks = Object.keys(contentBlocksStatus);
			for (let i = 0; i < blocks.length; i++) {
				const block = blocks[i];
				const blockStatus: FetchStatus = contentBlocksStatus[block];

				if (blockStatus === FetchStatus.FETCHING) {
					return true;
				}
			}
		}
		return false;
	}

	getNavigationItems(props: FolderDriveItemListPageProps): NavigationItemData[] {
		const { galleryId, params } = props;
		const { originParam } = params;
		const isPrivateDir = this.isPrivateDir(props);
		const driveFolderId = originParam ? parseInt(originParam, 10) : galleryId;
		const isSpecialPrivateDir = driveFolderId && this.isSpecialPrivateDir(driveFolderId);

		return isPrivateDir && !isSpecialPrivateDir ? [
			this.getTutorialNavigationItem(props.locale),
			...myDocumentsNavItems,
		] : [];
	}

	getTutorialNavigationItem(locale: string): NavigationItemData {
		const tutorialsByLocale = {
			'ca_ES': {
				url: 'https://fr.zone-secure.net/149562/.uprez-tutorial',
				image: './.gallery/static/images/tutorial_uprez_cover_ES.jpg',
			},
			'en_UK': {
				url: 'https://fr.zone-secure.net/149574/1170924/',
				image: './.gallery/static/images/tutorial_uprez_cover_EN.jpg',
			},
			'es_ES': {
				url: 'https://fr.zone-secure.net/149562/.uprez-tutorial',
				image: './.gallery/static/images/tutorial_uprez_cover_ES.jpg',
			},
			'fr_FR': {
				url: 'https://fr.zone-secure.net/149574/1150789/',
				image: './.gallery/static/images/tutorial_uprez_cover_FR.jpg',
			},
			'de_DE': {
				url: 'https://fr.zone-secure.net/149574/1170924/',
				image: './.gallery/static/images/tutorial_uprez_cover_EN.jpg',
			},
			'it_IT': {
				url: 'https://fr.zone-secure.net/149574/1170924/',
				image: './.gallery/static/images/tutorial_uprez_cover_EN.jpg',
			},
		};
		return {
			label: 'tutorial_uprez',
			type: Api.DriveItemType.ExternalLink,
			...tutorialsByLocale[locale]
		};
	}

	isSpecialPrivateDir(resourceId: number) {
		const { specialPrivateDirIds } = this.props;
		return specialPrivateDirIds && specialPrivateDirIds.includes(resourceId);
	}

	isPrivateDir(props: FolderDriveItemListPageProps): boolean {
		return this.getDriveFolderId(props) === props.privateDirId;
	}

	isSharedDir(props: FolderDriveItemListPageProps): boolean {
		return this.getDriveFolderId(props) === props.sharedDirId;
	}

	renderAdditionalItems(): JSX.Element[] | null {
		return null;
	}

	getAdditionalItemsLength(): number {
		return 0;
	}
}

const mapStateToProps = ({
	driveList,
	galleryInfos: {
		config: galleryConfig
	},
	i18n: {
		locale
	}
}: StoreState) => {
	const privateDirId = Utils.getPrivateDirId(driveList, galleryConfig);
	const sharedDirId = Utils.getSharedDirId(driveList, galleryConfig);
	const sourcesDirId = Utils.getSourcesDirId(driveList, galleryConfig);
	const archiveDirId = Utils.getArchiveDirId(driveList, galleryConfig);
	const specialPrivateDirIds = galleryConfig.specialPrivateDirIds || [];

	return {
		privateDirId,
		sharedDirId,
		sourcesDirId,
		archiveDirId,
		locale,
		specialPrivateDirIds,
	};
};

export default connect(mapStateToProps)(FolderDriveItemListPage);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/DriveItemListPage/extends/Folder.tsx